<template>
  <div>
    <div v-if="notes" class="overlay">
      <b-row class="justify-content-center align-items-center notes-overlay">
        <b-col sm="12" md="4" class="p-4 overlay-card text-center">
          <b-form-select v-model="currentNotes.noteType" :options="noteTypes" class="mb-3"></b-form-select>
          <!-- lazy: syncing every keystroke re-renders all order rows (~60ms each) -->
          <b-form-textarea v-model="currentNotes.notes" lazy></b-form-textarea>
          <b-row class="mt-3">
            <b-col cols="6">
              <b-button
                @click="closeNotes"
                variant="secondary"
                block size="lg">
                Close
              </b-button>
            </b-col>
            <b-col cols="6">
              <b-button
                @click="saveNotes"
                variant="primary"
                block size="lg">
                Save
              </b-button>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
    <div class="page-head">
      <div>
        <h3>Processing Orders</h3>
        <p class="page-head-sub">Ordered parts grouped by day and customer</p>
      </div>
      <div class="total-chip">
        {{totalPrice.toFixed(2).toLocaleString('en')}} PLN <span>· {{totalCount}} pcs</span>
      </div>
    </div>
    <div class="toolbar align-items-center">

      <!--        <div class="w-20">-->
      <!--          <b-form-input-->
      <!--            class="bg-dark text-white"-->
      <!--            v-model="search"-->
      <!--            :disabled="!data.length"-->
      <!--            placeholder="SKU FILTER"></b-form-input>-->
      <!--        </div>-->
      <span class="toolbar-caption">Show</span>
      <button @click="setFilter('ready')" :class="['btn', 'filter-btn', filter === 'ready' ? 'btn-success' : 'btn-outline-success']">Ready</button>
      <button @click="setFilter('regos')" :class="['btn', 'filter-btn', filter === 'regos' ? 'btn-warning' : 'btn-outline-warning']">Local · Regos</button>
      <button @click="setFilter('localCompleted')" :class="['btn', 'filter-btn', filter === 'localCompleted' ? 'btn-info' : 'btn-outline-info']">Local · Completed</button>
      <button @click="setFilter('localReserved')" :class="['btn', 'filter-btn', filter === 'localReserved' ? 'btn-primary' : 'btn-outline-primary']">Local · Reserved</button>
    </div>
    <div v-if="loading" class="overlay">
      <div class="w-100 position-absolute text-center" style="top: 50%; transform: translateY(-50%);">
        <b-icon class="text-white" icon="arrow-clockwise" animation="spin" font-scale="3"></b-icon>
      </div>
    </div>

    <div v-if="data.length">
      <div class="row mx-0 overflow-auto">
        <div class="w-100">
          <table class="table orders-table">
            <thead>
            <tr>
              <th scope="col">#</th>
              <!--              <th scope="col">Added</th>-->
              <!--              <th scope="col">SKU</th>-->
              <th class="d-none d-sm-table-cell" scope="col">SSKU</th>
              <!--              <th class="d-none d-sm-table-cell" scope="col">Factory #</th>-->
              <th class="d-none d-sm-table-cell" scope="col">Name</th>
              <th class="d-none d-sm-table-cell" scope="col">Ordered</th>
              <th class="d-none d-sm-table-cell" scope="col">Scanned</th>
              <th class="d-none d-sm-table-cell text-center" scope="col">Ordered For</th>
              <th class="d-none d-sm-table-cell" scope="col">Notes</th>
            </tr>
            </thead>
            <tbody>
            <template v-for="(row, i) of filteredList">
              <!-- Gap between order cards (purely visual, see groupLayout) -->
              <tr v-if="groupLayout[i].gap" class="group-gap" :key="'gap-' + i" aria-hidden="true">
                <td colspan="10"></td>
              </tr>
              <!-- Date header row: print only when date changes, always before separator and order row.
                   Skipped in the local views because rows are sorted by shelf, not by date. -->
              <tr
                v-if="!shelfView && (i === 0 || moment.utc(row.addedAt).format('YYYY-MM-DD') !== moment.utc(filteredList[i-1].addedAt).format('YYYY-MM-DD'))"
                :key="'date-' + moment.utc(row.addedAt).format('YYYY-MM-DD') + '-' + i"
                class="date-row"
              >
                <td colspan="10" class="date-row-cell">
                  <b-icon-calendar3 class="mr-2"></b-icon-calendar3>
                  {{ moment.utc(row.addedAt).format('DD.MM.YYYY') }}
                </td>
              </tr>
              <!-- Separator row for metaData group, drawn from the group's header (see groupHeader).
                   Placed by groupLayout at the first *visible* line of each group, so a
                   filter that hides a group's first line doesn't drop its header.
                   Skipped in the local views: rows are re-sorted by lastLocation so the
                   header no longer sits at a group boundary. -->
              <tr v-if="groupLayout[i].header" class="separator" :key="'sep-' + i">
                <td colspan="10" :class="[getClassForMetadata(groupLayout[i].header), 'w-100']">
                    <span
                      class="text-uppercase"
                      v-b-tooltip.hover.right="`Ordered: ${moment.utc(groupLayout[i].header.addedAt).tz('Europe/Oslo').format('DD.MM.YYYY HH:mm')}`">
                      {{groupLayout[i].header.fromWeb ? 'website' : groupLayout[i].header.user && groupLayout[i].header.user.username || 'Lyngdal'}} - {{groupLayout[i].header.metaData}} {{groupLayout[i].header.packingBox ? `[${groupLayout[i].header.packingBox.boxName}]` : ''}}
                    </span>
                  <span
                    @click="openBoxAssignment(groupLayout[i].header)"
                    class="ml-3 box-action-icon"
                    v-b-tooltip.hover.left="groupLayout[i].header.packingBox ? 'Change box assignment' : 'Assign packing box'">
                    <b-icon :icon="groupLayout[i].header.packingBox ? 'tags-fill' : 'tag'" :font-scale="1.1"></b-icon>
                  </span>
                  <span v-if="groupLayout[i].header.fromWeb && groupLayout[i].header.deliveryMethod" class="ml-3 float-right">{{groupLayout[i].header.deliveryMethod.name}}</span>
                </td>
              </tr>
              <!-- Order row -->
              <tr :key="i" :class="['article-row', getRowClass(row), { 'group-last': groupLayout[i].last }]">
                <td>{{i+1}}</td>
                <td>{{row.ssku}}</td>
                <td v-b-tooltip.hover.left="row.supplier || ''">
                  {{row.name}}
                </td>
                <!--                  <td>{{row.ssku}}</td>-->
                <!--                  <td>{{row.num}}</td>-->
                <td class="text-center">{{row.count}} - {{!row.processed ? 'O' : (row.replacedWithLocal ? 'L' : 'P')}}
                  <br v-if="row.replacedWithLocal && row.lastLocation">{{row.lastLocation}}
                </td>
                <td class="text-center">{{row.confirmed || 0}}</td>
                <td class="text-center">{{row.metaData || 'WORKSHOP'}}</td>
                <td v-b-tooltip.hover.left="row.notes ? stripHtmlTags(row.notes) : ''" class="text-center" @click="openNotes(row)">
                  <b-icon icon="pencil-square" class="pointer"></b-icon>
                </td>
              </tr>
            </template>

            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div v-else-if="!data.length && !loading" class="empty-state">
      <b-icon-check2-all></b-icon-check2-all>
      All orders processed
    </div>

    <b-modal
      id="box-select-modal"
      :title="currentBoxMetadata.currentBox ? 'Change Packing Box 📦' : 'Select Packing Box 📦'"
      @ok="handleBoxSelection"
      ok-title="Select">
      <div v-if="currentBoxMetadata.currentBox" class="mb-3 p-2 bg-warning text-dark rounded">
        <strong>Current Box:</strong> {{ currentBoxMetadata.currentBox }}
      </div>
      <v-select
        v-model="selectedBox"
        :options="boxOptions"
        :reduce="option => option.id"
        :searchable="false"
        placeholder="Please select available box..."
      >
        <template #option="option">
          <div :class="['box-option', option.colorClass]">
            {{ option.boxName }}
          </div>
        </template>

        <!-- keeps the same color on the selected value in the input -->
        <template #selected-option="option">
          <div :class="['box-option', option.colorClass]">
            {{ option.boxName }}
          </div>
        </template>
      </v-select>
    </b-modal>

  </div>
</template>

<script>
import moment from 'moment';
import 'moment-timezone';
import axios from 'axios';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

import { metadataMapping, orderLead } from '@/utils';
import fixed from '../fixed';

export default {
  components: {
    vSelect,
  },
  data: () => ({
    loading: false,
    data: [],
    totalCount: 0,
    totalPrice: 0,
    search: '',
    filter: null,

    notes: false,
    // count vs confirmed per order group, kept in sync by getOrders and used
    // by the READY filter. Confirmations themselves happen on /mobile-scan.
    allScanned: {},
    currentNotes: {
      id: '',
      noteType: 0,
      notes: '',
    },

    noteTypes: [
      { value: 0, text: 'Other' },
      { value: 1, text: 'EAN code issue' },
      { value: 2, text: 'Unavailable goods' },
      { value: 3, text: 'Goods replaced with' },
      { value: 4, text: 'Item canceled' },
    ],

    packingBoxes: [],
    selectedBox: null,
    currentBoxMetadata: {
      metaData: null,
      addedAt: null,
      currentBox: null,
      currentBoxId: null,
    },
    moment,
  }),
  computed: {
    // Where each order group starts and ends in the visible list: drives the
    // separator header, the gap between order cards and their rounded corners.
    // A group is one day + exact (trimmed) metaData, same as getOrders builds.
    // Its header is looked up by that key rather than read off the group's
    // first line, because filters such as 'regos' can hide that line, which
    // used to drop the header and run the rest under the previous order.
    // The local views re-sort by shelf, so they have no groups.
    groupLayout() {
      const list = this.filteredList;
      const grouped = !this.shelfView;
      const key = (r) => `${moment.utc(r.addedAt).format('YYYY-MM-DD')}|${(r.metaData || '').trim()}`;
      const headers = {};
      this.data.forEach((r) => { if (r.header) headers[key(r)] = r.header; });
      const starts = (i) => grouped && (i === 0 || key(list[i]) !== key(list[i - 1]));
      return list.map((row, i) => ({
        header: starts(i) ? headers[key(row)] || null : null,
        gap: i > 0 && starts(i),
        last: i === list.length - 1 || starts(i + 1),
      }));
    },
    shelfView() {
      return this.filter === 'localCompleted' || this.filter === 'localReserved';
    },
    // Orders still waiting for parts from outside local stock. Canceled and
    // unavailable lines don't count: they are saved with confirmed = count.
    waitingOrders() {
      const waiting = this.data.filter((row) => !row.replacedWithLocal && (row.confirmed || 0) < row.count);
      return new Set(waiting.map((row) => this.getCompositeKey(row)));
    },
    filteredList() {
      switch (this.filter) {
        case 'ready':
          return this.data.filter((row) => {
            const key = this.getCompositeKey(row);
            return this.allScanned[key] && this.allScanned[key].count === this.allScanned[key].confirmed;
          });
        case 'processing':
          return this.data.filter((row) => {
            const key = this.getCompositeKey(row);
            return this.allScanned[key] && this.allScanned[key].count !== this.allScanned[key].confirmed;
          });
        case 'regos':
          return this.data.filter((row) => {
            const isRegosOrder = row.metaData.toLowerCase().startsWith('regos_');
            const isNotFullyConfirmed = !row.confirmed || row.confirmed < row.count;

            return isRegosOrder && row.replacedWithLocal && isNotFullyConfirmed;
          });
        case 'localCompleted':
          return this.localPickList(false);
        case 'localReserved':
          return this.localPickList(true);
        default:
          return this.data.filter((row) => row.sku.match(this.search));
      }
    },
    boxOptions() {
      return this.packingBoxes.map((packing) => ({
        id: packing.id,
        boxName: `${packing.boxName} | Size: ${packing.boxSize}`,
        colorClass: this.getBoxColorClass(packing.boxSize),
      }));
    },
  },
  mounted() {
    this.getOrders();
  },
  methods: {
    // Helper method to generate composite key for order grouping (metaData + date)
    getCompositeKey(row) {
      const dateKey = moment.utc(row.addedAt).format('YYYY-MM-DD');
      return `${row.metaData.trim()}|${dateKey}`;
    },
    // Local lines still to collect, in shelf order, from orders that are
    // (or are not) waiting for other parts.
    localPickList(waiting) {
      return this.data
        .filter((row) => {
          const isNotFullyConfirmed = !row.confirmed || row.confirmed < row.count;
          return row.replacedWithLocal && isNotFullyConfirmed
            && this.waitingOrders.has(this.getCompositeKey(row)) === waiting;
        })
        .sort((a, b) => this.compareShelfLocation(a.lastLocation, b.lastLocation));
    },
    // Parse a shelf location like "11-V 4 A" into { letter: 'V', number: 11 }.
    // Anything that doesn't match returns nulls and will be pushed to the end
    // when compared.
    parseShelfLocation(loc) {
      if (!loc) return { letter: null, number: null };
      const m = String(loc).trim().match(/^(\d+)\s*-\s*([A-Za-z])/);
      if (!m) return { letter: null, number: null };
      return { number: parseInt(m[1], 10), letter: m[2].toUpperCase() };
    },
    // Sort by number first (ascending), then by letter ascending within the
    // same number (A before Z). Rows with unparseable / missing lastLocation
    // sink to the bottom.
    compareShelfLocation(a, b) {
      const pa = this.parseShelfLocation(a);
      const pb = this.parseShelfLocation(b);

      const aValid = pa.letter !== null;
      const bValid = pb.letter !== null;
      if (aValid !== bValid) return aValid ? -1 : 1;
      if (!aValid && !bValid) return 0;

      if (pa.number !== pb.number) return pa.number - pb.number;
      return pa.letter.localeCompare(pb.letter);
    },
    async getOrders() {
      this.search = '';
      this.totalCount = 0;
      this.totalPrice = 0;
      this.loading = true;

      try {
        const { data } = await axios.get(
          `${fixed.API_URL}/poland-processing`,
          { withCredentials: true },
        );
        if (data && data.length) {
          // Group by day first using UTC to avoid timezone issues
          const groupedByDay = {};
          data.forEach((row) => {
            const day = moment.utc(row.addedAt).format('YYYY-MM-DD');
            if (!groupedByDay[day]) groupedByDay[day] = [];
            groupedByDay[day].push(row);
          });

          // Sort days in descending order (newest first)
          const sortedDays = Object.keys(groupedByDay).sort((a, b) => new Date(b) - new Date(a));

          // Process each day
          this.data = [];
          sortedDays.forEach((day) => {
            // Group by metaData within each day. Exactly, not case-insensitively:
            // boxes, scanning and packing all match metaData exactly, so a group
            // here must be what ends up in one package.
            const metaGroups = {};
            groupedByDay[day].forEach((row) => {
              const meta = (row.metaData || '').trim();
              if (!metaGroups[meta]) metaGroups[meta] = [];
              metaGroups[meta].push(row);
            });

            // Get array of metadata groups and sort them by their most recent item
            const sortedGroups = Object.entries(metaGroups)
              .sort(([, groupA], [, groupB]) => {
                const oldestA = Math.min(...groupA.map((item) => moment.utc(item.addedAt).valueOf()));
                const oldestB = Math.min(...groupB.map((item) => moment.utc(item.addedAt).valueOf()));
                return oldestB - oldestA;
              });

            // Now process each sorted group
            sortedGroups.forEach(([, group]) => {
              // Sort items within group by addedAt descending
              group.sort((a, b) => moment.utc(b.addedAt).valueOf() - moment.utc(a.addedAt).valueOf());
              // The first row carries the separator drawn above the group.
              const [first] = group;
              first.header = this.groupHeader(group);
              this.data = this.data.concat(group);
            });
          });

          // reset confirmed counts
          this.allScanned = {};
          // calculate total order value and per-group scan progress
          for (let i = this.data.length - 1; i >= 0; i -= 1) {
            const row = this.data[i];
            this.totalPrice += (row.price * row.count);
            this.totalCount += row.count;

            // Store count vs confirmed, so we know when package is ready
            // Use composite key: metaData + date to match backend grouping
            const compositeKey = this.getCompositeKey(row);
            if (!this.allScanned[compositeKey]) {
              this.allScanned[compositeKey] = { count: row.count, confirmed: row.confirmed || 0 };
            } else {
              this.allScanned[compositeKey].count += row.count;
              this.allScanned[compositeKey].confirmed += row.confirmed || 0;
            }
          }
        } else {
          throw new Error('No more orders to process 😴');
        }
      } catch (error) {
        this.data = [];
        if (error.response && error.response.status === 403) {
          this.$toast.warning('you\'re not authorised for this action ✋🏻');
        } else {
          this.$toast.error(error?.message);
        }
      }
      this.loading = false;
    },
    // What a group's separator shows. A web order keeps its customer, delivery
    // method and colour when lines are added to it by hand, and its box is
    // shown whichever of its lines carries it (lines added after the box was
    // assigned don't).
    groupHeader(group) {
      const lead = orderLead(group);
      const withBox = group.find((r) => r.packingBox);
      return {
        metaData: lead.metaData,
        addedAt: lead.addedAt,
        fromWeb: lead.fromWeb,
        user: lead.user,
        orderedFor: lead.orderedFor,
        deliveryMethod: lead.deliveryMethod,
        packingBox: withBox ? withBox.packingBox : null,
      };
    },
    getBoxColorClass(boxSize) {
      switch (boxSize) {
        case 2:
          return 'option-red';
        case 3:
          return 'option-blue';
        default:
          return '';
      }
    },
    async getPackingBoxes() {
      try {
        const { data } = await axios.get(`${fixed.API_URL}/admin/pack-boxes`, { withCredentials: true });
        this.packingBoxes = data.filter((box) => !box.occupied) || [];
      } catch (error) {
        this.$toast.error(error?.response?.data || 'error while fetching packing boxes');
      }
    },
    async occupyPackingBox(box, order, oldBoxId = null) {
      if (!order.metaData || !order.addedAt) return false;

      try {
        const payload = {
          boxID: box,
          metaData: order.metaData,
          addedAt: order.addedAt,
        };

        // Add oldBoxID if provided (for reassignment)
        if (oldBoxId) {
          payload.oldBoxID = oldBoxId;
        }

        const { data } = await axios.put(
          `${fixed.API_URL}/admin/occupy-box`,
          payload,
          { withCredentials: true },
        );
        return data;
      } catch (error) {
        this.$toast.error(error?.response?.data || 'error while occupying packing box');
        return null;
      }
    },
    // header is a group's header (groupHeader). The server assigns the box to
    // every line with this exact metaData on the header's day: the same
    // metaData can have orders on different dates, each with its own box.
    async openBoxAssignment(header) {
      if (!header.metaData) {
        this.$toast.error('Invalid order data: missing metaData');
        return;
      }

      this.currentBoxMetadata = {
        metaData: header.metaData,
        addedAt: header.addedAt,
        currentBox: header.packingBox ? header.packingBox.boxName : null,
        currentBoxId: header.packingBox ? header.packingBox.id : null,
      };

      await this.getPackingBoxes();

      // Do NOT pre-select the current box: it is already shown in the yellow
      // banner inside the modal, so pre-selecting it is just visual noise and
      // the user should make an explicit choice.
      this.selectedBox = null;

      this.$bvModal.show('box-select-modal');
    },
    async handleBoxSelection(bvModalEvent) {
      bvModalEvent.preventDefault();

      if (!this.selectedBox) {
        this.$toast.warning('Please select one of the available boxes');
        return;
      }

      try {
        const result = await this.occupyPackingBox(
          this.selectedBox,
          {
            metaData: this.currentBoxMetadata.metaData,
            addedAt: this.currentBoxMetadata.addedAt,
          },
          this.currentBoxMetadata.currentBoxId,
        );

        if (result) {
          const action = this.currentBoxMetadata.currentBoxId ? 'changed to' : 'assigned to';
          this.$toast.success(`Box ${action} ${result.boxName} for ${this.currentBoxMetadata.metaData}`);

          await this.getOrders();
          this.$bvModal.hide('box-select-modal');
        }
      } catch (error) {
        this.$toast.error('Failed to assign box');
      }
    },
    openNotes(record) {
      // eslint-disable-next-line no-underscore-dangle
      this.currentNotes.id = record._id;
      this.currentNotes.noteType = record.noteType || 0;
      this.currentNotes.notes = record.notes;
      this.notes = true;
    },
    closeNotes() {
      this.currentNotes.id = '';
      this.currentNotes.notes = '';
      this.notes = false;
    },
    async saveNotes() {
      if (this.currentNotes.id && this.currentNotes.notes) {
        this.loading = true;
        try {
          await axios.post(
            `${fixed.API_URL}/poland-order-comment`, this.currentNotes,
            { withCredentials: true },
          );
          await this.getOrders();
          this.$toast.success('Notes saved successfully');
        } catch (error) {
          if (error.response && error.response.status === 403) {
            this.$toast.error('you\'re not authorised for this action ✋🏻');
          } else {
            this.$toast.error('can\'t save notes for the article');
          }
        }
        this.loading = false;
      } else {
        this.$toast.warning('Note content is required');
      }

      this.currentNotes.id = '';
      this.currentNotes.notes = '';
      this.notes = false;
    },
    getClassForMetadata(order) {
      if (order.orderedFor) return 'bg-dark';
      const mapped = metadataMapping.find((m) => m.pattern.test(order.metaData));
      return mapped.getClass(order?.deliveryMethod?.name);
    },
    getRowClass(row) {
      if (row.notes) {
        switch (row.noteType) {
          case 0: return 'bg-primary';
          case 1: return 'wrong-ean text-white';
          case 2: return 'unavailable-goods text-white';
          case 3: return 'goods-replaced-with text-dark';
          case 4: return 'bg-danger text-muted';

          default: return '';
        }
      }

      if (row.count === row.confirmed) return 'bg-success text-dark';
      if (row.count < row.confirmed) return 'bg-warning text-dark';
      return '';
    },
    getWeight(product) {
      if (!product?.parameters || !product.parameters.length) return 'No info';

      for (let i = 0; i < product.parameters.length; i += 1) {
        if (product.parameters[i]?.meaning && product.parameters[i].meaning === 'weight') {
          const normalizedStr = product.parameters[i].value.replace(',', '.');
          const num = parseFloat(normalizedStr);
          if (!Number.isNaN(num)) {
            return num.toFixed(2);
          }

          return 'Invalid weight';
        }
      }

      return 'No Info';
    },
    stripHtmlTags(html) {
      const div = document.createElement('div');
      div.innerHTML = html;
      return div.textContent || div.innerText || '';
    },
    setFilter(f) {
      this.filter = this.filter === f ? this.filter = null : this.filter = f;
    },
  },
};
</script>

<style>
.overlay {
  position: fixed;
  margin: 0;
  padding: 0;
  left: 0;
  z-index: 10;
  width: 100%;
  overflow: hidden;

  background-color: rgba(0, 0, 0, 0.9);
}

.notes-overlay {
  height: 100vh;
}

.overlay-card {
  background: var(--surface);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
}

.total-chip {
  padding: 0.5rem 1rem;
  border-radius: var(--radius-sm);
  background: rgba(47, 184, 102, 0.14);
  border: 1px solid rgba(47, 184, 102, 0.35);
  color: #7ee0a5;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.total-chip span {
  color: var(--text-muted);
  font-weight: 500;
}

.toolbar-caption {
  color: var(--text-muted);
  font-size: 0.85rem;
  font-weight: 600;
}

.filter-btn {
  padding: 0.35rem 1rem;
}

.order-label {
  background-color: var(--danger);
  top: -21px;
}

.wrong-ean {
  background-color: #8B4513FF !important;
}
.unavailable-goods {
  background-color: #45237a !important;
}
.goods-replaced-with {
  background-color: #FFFF00FF !important;
}

.box-action-icon {
  cursor: pointer;
  color: inherit;
  opacity: 0.7;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.box-action-icon:hover {
  opacity: 1;
  transform: scale(1.15);
}

#box-select-modal .modal-dialog {
  margin-top: 50px;
}

/* box size chips inside the vue-select dropdown */
.box-option {
  padding: 4px 8px;
  border-radius: 4px;
}

.option-red {
  background-color: #ffcccc;
  color: #cc0000;
}

.option-blue {
  background-color: #cce0ff;
  color: #0044cc;
}

</style>
